import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { getProviders } from '../../../api/providers';

export default function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['providers'],
    queryFn: getProviders,
  });

  const provider = data?.data.find(
    (item: import('../../../api/providers').Provider) => item.id === id
  );

  const pageTitle = provider?.businessName || 'Provider detail';
  const pageSubtitle = provider
    ? [provider.city, provider.postcode].filter(Boolean).join(' · ') || 'Provider record'
    : 'Provider record';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Link
          to="/dashboard/providers"
          style={{
            textDecoration: 'none',
            color: '#4f46e5',
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          ← Back to providers
        </Link>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>{pageTitle}</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.98rem' }}>{pageSubtitle}</p>
        </div>
      </div>

      {isLoading && (
        <MessageCard tone="neutral">
          Loading provider details...
        </MessageCard>
      )}

      {isError && (
        <MessageCard tone="error">
          <div>
            Failed to load provider details:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </div>

          <button
            type="button"
            onClick={() => refetch()}
            style={buttonStyle}
          >
            Retry
          </button>
        </MessageCard>
      )}

      {!isLoading && !isError && !provider && (
        <MessageCard tone="neutral">
          No provider record matched the selected route parameter.
        </MessageCard>
      )}

      {!isLoading && !isError && provider && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SummaryCard provider={provider} />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              alignItems: 'start',
            }}
          >
            <SectionCard title="Business">
              <InfoRow label="Business name" value={provider.businessName || 'Unnamed provider'} />
              <InfoRow label="Status" value={formatStatus(provider.status)} />
            </SectionCard>

            <SectionCard title="Contact">
              <InfoRow label="Contact name" value={provider.contactName ?? 'Not set'} />
              <InfoRow label="Email" value={provider.email ?? 'Not set'} />
              <InfoRow label="Phone" value={provider.phone ?? 'Not set'} />
            </SectionCard>

            <SectionCard title="Location">
              <InfoRow label="City" value={provider.city ?? 'Not set'} />
              <InfoRow label="Postcode" value={provider.postcode ?? 'Not set'} />
            </SectionCard>

            <SectionCard title="Audit">
              <InfoRow label="Provider ID" value={provider.id} />
              <InfoRow label="Created at" value={formatDateTime(provider.createdAt)} />
              <InfoRow label="Updated at" value={formatDateTime(provider.updatedAt)} />
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}

type Provider = import('../../../api/providers').Provider;

function SummaryCard({ provider }: { provider: Provider }) {
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Provider record
        </span>
        <h2 style={{ margin: 0, fontSize: '1.35rem', color: '#111827' }}>
          {provider.businessName || 'Unnamed provider'}
        </h2>
        <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.6 }}>
          {[
            provider.city || 'City not set',
            provider.postcode || 'Postcode not set',
          ].join(' · ')}
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <Pill label={`Status: ${formatStatus(provider.status)}`} />
        <Pill label={provider.email ? 'Email available' : 'Email not set'} />
        <Pill label={provider.phone ? 'Phone available' : 'Phone not set'} />
      </div>
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: '1rem',
          color: '#111827',
        }}
      >
        {title}
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{children}</div>
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      <span style={{ fontSize: '0.86rem', fontWeight: 600, color: '#64748b' }}>{label}</span>
      <span style={{ color: '#111827', lineHeight: 1.6, wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function MessageCard({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'neutral' | 'error';
}) {
  const isError = tone === 'error';

  return (
    <div
      style={{
        background: isError ? '#fef2f2' : '#ffffff',
        border: `1px solid ${isError ? '#fecaca' : '#e5e7eb'}`,
        borderRadius: '14px',
        padding: '18px',
        color: isError ? '#b91c1c' : '#4b5563',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {children}
    </div>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: '999px',
        background: '#eef2ff',
        color: '#4338ca',
        fontSize: '0.9rem',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function formatStatus(value?: string | null) {
  if (!value) return 'Not set';

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value?: string) {
  if (!value) return 'Not available';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const buttonStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  background: '#111827',
  color: '#ffffff',
  border: 'none',
  borderRadius: '10px',
  padding: '10px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};